/**
 * HubSpot Forms Integration
 *
 * Utilities for loading the HubSpot forms embed script and
 * detecting HubSpot embeds in CMS page HTML.
 */

export const HUBSPOT_PORTAL_ID = process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID || '5417380';

/**
 * Portal id to use for a browser-side embed. Typed-CMS content files embed
 * the production portal id verbatim (byte-for-byte parity policy), so the
 * env var must win over a content-supplied value at runtime — otherwise a
 * form submitted on staging writes to the production CRM. Unset env
 * (production) keeps the content value / prod default unchanged.
 */
export function resolveHubSpotPortalId(portalId?: string): string {
  return process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID || portalId || '5417380';
}
export const HUBSPOT_REGION = 'na1';
export const HUBSPOT_FORMS_SCRIPT_URL = 'https://js.hsforms.net/forms/embed/v2.js';
export const HUBSPOT_QUOTE_FORM_ID = process.env.NEXT_PUBLIC_HUBSPOT_QUOTE_FORM_ID || 'f9825966-73cb-4cf8-a7e5-4ee2fe4cd09a';

// ============================================================================
// Script Loader
// ============================================================================

let scriptLoadPromise: Promise<void> | null = null;

/**
 * Load the HubSpot forms embed script (once, cached).
 * Returns a promise that resolves when the script is ready.
 */
export function loadHubSpotForms(): Promise<void> {
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve, reject) => {
    // Check if already loaded
    if (typeof window !== 'undefined' && (window as unknown as Record<string, unknown>).hbspt) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = HUBSPOT_FORMS_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoadPromise = null; // Allow retry
      reject(new Error('Failed to load HubSpot forms script'));
    };
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

// ============================================================================
// HTML Detection & Replacement
// ============================================================================

interface HubSpotFormMatch {
  formId: string;
  portalId: string;
  region: string;
}

/**
 * Extract HubSpot form IDs from hbspt.forms.create() script blocks
 * and replace them with placeholder divs.
 */
export function replaceHubSpotFormScripts(html: string): {
  html: string;
  forms: HubSpotFormMatch[];
} {
  const forms: HubSpotFormMatch[] = [];

  // Match <script> blocks containing hbspt.forms.create()
  const formScriptRegex = /<script[^>]*>[\s\S]*?hbspt\.forms\.create\(\s*\{([^}]*)\}\s*\)[\s\S]*?<\/script>/gi;

  const replaced = html.replace(formScriptRegex, (match, configBody: string) => {
    // Also match the preceding forms embed script tag if present
    const formId = extractQuotedValue(configBody, 'formId');
    const portalId = extractQuotedValue(configBody, 'portalId') || HUBSPOT_PORTAL_ID;
    const region = extractQuotedValue(configBody, 'region') || HUBSPOT_REGION;

    if (!formId) return match; // Can't extract form ID, leave as-is

    forms.push({ formId, portalId, region });
    return `<div data-hubspot-form-id="${formId}" data-hubspot-portal-id="${portalId}" data-hubspot-region="${region}"></div>`;
  });

  // Also remove the forms embed script tag (//js.hsforms.net/...)
  const cleanedHtml = replaced.replace(
    /<script[^>]*src=["'][^"']*hsforms\.net[^"']*["'][^>]*><\/script>/gi,
    ''
  );

  return { html: cleanedHtml, forms };
}

/**
 * Replace <object data="https://thankyou.getscw.com/..."> with iframe placeholders.
 * Handles multi-line <object> tags where attributes span multiple lines.
 */
export function replaceHubSpotObjects(html: string): string {
  // Match <object ...data="thankyou.getscw.com/..."...>...</object> (multi-line)
  const objectRegex = /<object[\s\S]*?data=["'](https:\/\/thankyou\.getscw\.com\/[^"']+)["'][\s\S]*?<\/object>/gi;

  return html.replace(objectRegex, (_match, url: string) => {
    return `<div data-hubspot-iframe="${url}" style="width:100%;min-height:600px;"></div>`;
  });
}

// ============================================================================
// Helpers
// ============================================================================

function extractQuotedValue(text: string, key: string): string | null {
  // Match key: "value" or key: 'value' (with optional quotes around key)
  const regex = new RegExp(`["']?${key}["']?\\s*:\\s*["']([^"']+)["']`, 'i');
  const match = text.match(regex);
  return match?.[1] || null;
}
